import { Link } from "react-router-dom";
import { Mail, Instagram, Twitter, Youtube, Video } from "lucide-react";
import { AFFILIATE_DISCLAIMER, CONTACT_EMAIL } from "@/components/common/Disclaimers";
import { useLanguage } from "@/contexts/LanguageContext";

// Pinterest SVG icon (not available in lucide-react)
const PinterestIcon = ({ className }) => (
  <svg className={className} viewBox="0 0 24 24" fill="currentColor">
    <path d="M12 0C5.373 0 0 5.373 0 12c0 5.084 3.163 9.426 7.627 11.174-.105-.949-.2-2.405.042-3.441.218-.937 1.407-5.965 1.407-5.965s-.359-.719-.359-1.782c0-1.668.967-2.914 2.171-2.914 1.023 0 1.518.769 1.518 1.69 0 1.029-.655 2.568-.994 3.995-.283 1.194.599 2.169 1.777 2.169 2.133 0 3.772-2.249 3.772-5.495 0-2.873-2.064-4.882-5.012-4.882-3.414 0-5.418 2.561-5.418 5.207 0 1.031.397 2.138.893 2.738a.36.36 0 0 1 .083.345l-.333 1.36c-.053.22-.174.267-.402.161-1.499-.698-2.436-2.889-2.436-4.649 0-3.785 2.75-7.262 7.929-7.262 4.163 0 7.398 2.967 7.398 6.931 0 4.136-2.607 7.464-6.227 7.464-1.216 0-2.359-.632-2.75-1.378l-.748 2.853c-.271 1.043-1.002 2.35-1.492 3.146C9.57 23.812 10.763 24 12 24c6.627 0 12-5.373 12-12S18.627 0 12 0z"/>
  </svg>
);

// Social links
const socialLinks = [
  { name: "Instagram", href: "https://www.instagram.com/Nomadlivinglab", icon: Instagram },
  { name: "Pinterest", href: "https://www.pinterest.com/Nomadlivinglab", icon: PinterestIcon },
  { name: "X (Twitter)", href: "https://x.com/nomadlivinglab", icon: Twitter },
  { name: "YouTube", href: "https://www.youtube.com/@nomadlivinglab", icon: Youtube },
  { name: "TikTok", href: "https://www.tiktok.com/@nomadlivinglab", icon: Video },
];

const Footer = () => {
  const { language, t } = useLanguage();
  
  // Build language-aware link
  const buildLink = (path) => {
    if (language === 'en') return path;
    return `/${language}${path}`;
  };

  // Translated categories
  const mainCategories = [
    { name: t('nav_workstations'), slug: "workstations" },
    { name: t('nav_connectivity'), slug: "connectivity" },
    { name: t('nav_displays'), slug: "displays" },
    { name: t('nav_power'), slug: "power" },
    { name: t('nav_travel_setup'), slug: "travel-setup" },
    { name: t('nav_accessories'), slug: "accessories" },
  ];

  const workstationCategories = [
    { name: t('nav_temporary_desk'), slug: "temporary-desk-setup" },
    { name: t('nav_hotel_room'), slug: "hotel-room-setup" },
    { name: t('nav_compact_workstations'), slug: "compact-workstations" },
    { name: t('nav_dual_monitor'), slug: "dual-monitor-laptop-setup" },
  ];

  const seoCategories = [
    { name: t('seo_laptop_setup'), slug: "laptop-setup" },
    { name: t('seo_temporary_workspaces'), slug: "temporary-workspaces" },
    { name: t('seo_dual_monitor'), slug: "dual-monitor-setup" },
    { name: t('seo_portable_office'), slug: "portable-office" },
    { name: t('seo_work_from_hotel'), slug: "work-from-hotel" },
    { name: t('seo_mobile_connectivity'), slug: "mobile-connectivity" },
  ];

  return (
    <footer 
      className="bg-[#1A2B3C] text-white"
      data-testid="main-footer"
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-10">
          {/* Brand */}
          <div className="lg:col-span-1">
            <Link to={buildLink("/")} className="inline-flex items-center gap-3" data-testid="footer-logo">
              <img 
                src="/logo.png" 
                alt="NOMAD LIVING LAB" 
                className="h-12 w-12 object-contain bg-white rounded-lg p-1"
              />
              <span className="font-['Libre_Baskerville'] text-xl font-bold">
                NOMAD LIVING LAB
              </span>
            </Link>
            <p className="mt-4 text-white/70 text-sm leading-relaxed">
              {t('footer_description')}
            </p>
            <a 
              href={`mailto:${CONTACT_EMAIL}`}
              className="mt-4 flex items-center gap-2 text-white/70 hover:text-white transition-colors"
            >
              <Mail className="h-4 w-4" />
              <span className="text-sm">{CONTACT_EMAIL}</span>
            </a>
            
            {/* Social Links */}
            <div className="mt-4 flex items-center gap-3">
              {socialLinks.map((social) => (
                <a
                  key={social.name}
                  href={social.href}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="w-8 h-8 rounded-full bg-white/10 flex items-center justify-center hover:bg-white/20 transition-colors"
                  aria-label={social.name}
                  data-testid={`footer-social-${social.name.toLowerCase()}`}
                >
                  <social.icon className="h-4 w-4" />
                </a>
              ))}
            </div>
          </div>

          {/* Equipment Categories */}
          <div>
            <h4 className="font-['Libre_Baskerville'] font-bold mb-4">{t('footer_equipment')}</h4>
            <ul className="space-y-2">
              {mainCategories.map((cat) => (
                <li key={cat.slug}>
                  <Link 
                    to={buildLink(`/category/${cat.slug}`)}
                    className="text-white/70 hover:text-white text-sm transition-colors"
                    data-testid={`footer-cat-${cat.slug}`}
                  >
                    {cat.name}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Workstation Types */}
          <div>
            <h4 className="font-['Libre_Baskerville'] font-bold mb-4">{t('footer_workstations')}</h4>
            <ul className="space-y-2">
              {workstationCategories.map((cat) => (
                <li key={cat.slug}>
                  <Link 
                    to={buildLink(`/category/${cat.slug}`)}
                    className="text-white/70 hover:text-white text-sm transition-colors"
                    data-testid={`footer-ws-${cat.slug}`}
                  >
                    {cat.name}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* SEO Categories */}
          <div>
            <h4 className="font-['Libre_Baskerville'] font-bold mb-4">{t('footer_setup_guides')}</h4>
            <ul className="space-y-2">
              {seoCategories.map((cat) => (
                <li key={cat.slug}>
                  <Link 
                    to={buildLink(`/category/${cat.slug}`)}
                    className="text-white/70 hover:text-white text-sm transition-colors"
                    data-testid={`footer-seo-${cat.slug}`}
                  >
                    {cat.name}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Quick Links & Trust */}
          <div>
            <h4 className="font-['Libre_Baskerville'] font-bold mb-4">{t('footer_resources')}</h4>
            <ul className="space-y-2">
              <li>
                <Link 
                  to={buildLink("/blog")}
                  className="text-white/70 hover:text-white text-sm transition-colors"
                  data-testid="footer-blog"
                >
                  {t('footer_all_guides')}
                </Link>
              </li>
              <li>
                <Link
                  to={buildLink("/services")}
                  className="text-white/70 hover:text-white text-sm transition-colors"
                  data-testid="footer-services"
                >
                  Services
                </Link>
              </li>
              <li>
                <Link
                  to={buildLink("/about")}
                  className="text-white/70 hover:text-white text-sm transition-colors"
                  data-testid="footer-about"
                >
                  {t('footer_about_us')}
                </Link>
              </li>
              <li>
                <Link 
                  to={buildLink("/contact")}
                  className="text-white/70 hover:text-white text-sm transition-colors"
                  data-testid="footer-contact"
                >
                  {t('contact')}
                </Link>
              </li>
            </ul>
            
            <div className="mt-6">
              <div className="inline-flex items-center gap-2 px-3 py-2 rounded-lg bg-white/10 text-xs">
                <span className="w-2 h-2 rounded-full bg-[#2A9D8F]"></span>
                <span>{t('footer_independently_tested')}</span>
              </div>
            </div>
          </div>
        </div>

        {/* Affiliate Disclosure */}
        <div 
          className="mt-12 pt-8 border-t border-white/10"
          data-testid="affiliate-disclosure"
        >
          <p className="text-white/50 text-xs leading-relaxed">
            <strong>{t('footer_affiliate_disclosure')}:</strong> {AFFILIATE_DISCLAIMER} {t('footer_affiliate_text')}
          </p>
        </div>

        {/* Copyright & Legal Links */}
        <div className="mt-8 pt-8 border-t border-white/10 flex flex-col sm:flex-row justify-between items-center gap-4">
          <p className="text-white/50 text-xs sm:text-sm">
            © {new Date().getFullYear()} NOMAD LIVING LAB. {t('footer_all_rights')}
          </p>
          <div className="flex flex-wrap justify-center gap-3 sm:gap-4 text-white/50 text-xs sm:text-sm">
            <Link to={buildLink("/about")} className="hover:text-white transition-colors">{t('about')}</Link>
            <Link to={buildLink("/privacy-policy")} className="hover:text-white transition-colors">{t('footer_privacy_policy')}</Link>
            <Link to={buildLink("/terms-of-service")} className="hover:text-white transition-colors">{t('footer_terms')}</Link>
            <Link to={buildLink("/cookie-policy")} className="hover:text-white transition-colors">{t('footer_cookies')}</Link>
            <Link to={buildLink("/affiliate-disclosure")} className="hover:text-white transition-colors">{t('footer_affiliate_disclosure')}</Link>
            <Link to={buildLink("/contact")} className="hover:text-white transition-colors">{t('contact')}</Link>
            <Link to={buildLink("/media-kit")} className="hover:text-white transition-colors">Media Kit</Link>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
